import { type Evaluated } from '@/domain/construction';
import { type Bounds } from '@/domain/geometry';
import { type EntityId } from '@/domain/model';

import { pickBoundsOf } from './pickBounds';

/**
 * Where things are, so picking does not have to ask everything.
 *
 * ## The measurement that motivated it
 *
 * Hit-testing was a linear sweep: every pointer move computed the exact distance from the
 * cursor to every entity in the document. On a 8 000-entity scene that measured **3.0 ms per
 * query**, and a construction tool queries on every move. A 120 Hz frame has 8.3 ms in total,
 * so picking alone was eating a third of the budget before anything was drawn — and it grew
 * linearly, so the drawing that finally broke it would be the one a teacher spent an hour on.
 *
 * ## Why a uniform grid rather than an R-tree
 *
 * The architecture notes said R-tree, and a grid is the better answer here:
 *
 * - **Updates are the hot path, not queries.** Dragging re-evaluates a subtree on every move,
 *   so the index changes constantly. A grid inserts and removes in O(1) with no rebalancing;
 *   an R-tree degrades as it is updated and needs periodic bulk reloading to recover.
 * - **The query is a tiny box.** Picking asks about a circle a few pixels wide. That touches
 *   one or two cells, which is the case a grid is best at and an R-tree's descent overhead is
 *   worst at.
 * - **Geometry drawings are spatially even.** The pathological input for a grid is heavy
 *   clustering with vast empty space, which is what maps and CAD assemblies look like — not
 *   what a triangle with its circumcircle looks like.
 *
 * The cell size adapts to the content so this holds as the drawing grows.
 *
 * ## Unbounded values are a separate list, not a huge rectangle
 *
 * A line has no bounds. Giving it an enormous box would put it in every cell it crosses —
 * thousands of them — and re-inserting it on every update. Instead the unbounded values live
 * in a list that every query checks in full. There are only ever a handful of them in a real
 * drawing, and correctness does not depend on guessing how far a line "really" extends.
 *
 * ## What a query promises
 *
 * A **superset**. The grid answers "these cells overlap your box", which admits entities whose
 * bounding box overlaps but whose geometry does not — an arc's box contains its chord. The
 * caller still measures the true distance. The index removes work; it never decides a hit.
 */

export interface SpatialIndex {
  /** World units per cell. */
  readonly cell: number;
  /** Cell key → the entities whose bounds touch that cell. */
  readonly cells: ReadonlyMap<number, readonly EntityId[]>;
  /** Which cells each entity occupies, so removal does not have to search. */
  readonly placement: ReadonlyMap<EntityId, readonly number[]>;
  /** Lines and rays: no bounds, so every query must consider them. */
  readonly unbounded: readonly EntityId[];
}

/**
 * The default cell size, in world units.
 *
 * Used when the content gives no better answer — an empty document, or one holding only
 * lines. The value is the order of magnitude of a drawn figure rather than of the whole
 * canvas, because a cell much larger than the objects in it degenerates to a linear scan.
 */
const DEFAULT_CELL = 100;

/** Beyond this many cells for one entity, treat it as unbounded rather than paint the grid. */
const MAX_CELLS_PER_ENTITY = 256;

/**
 * The most cells one bounds query will walk before giving up and returning everything.
 *
 * Bounds queries come from viewport culling, where the caller re-checks each candidate
 * exactly — so surrendering is a performance choice with no effect on what is drawn.
 */
const MAX_CELLS_SCANNED = 4096;

/**
 * Cells are keyed by a single number rather than a `"x,y"` string.
 *
 * String keys were the first version and cost more than the query they served: every insert
 * and every lookup allocated and hashed a string. Packing two signed 16-bit cell coordinates
 * into one integer keeps `Map` on its fast integer path.
 *
 * The consequence is a hard limit of ±32 768 cells from the origin — at the default cell size
 * that is ±3.2 million world units, and coordinates beyond it wrap onto other cells. Wrapping
 * is safe rather than wrong: two distant regions share a cell, so a query there returns a
 * larger superset and the exact distance test still decides. Nothing is ever missed.
 */
const CELL_BITS = 16;
const CELL_MASK = (1 << CELL_BITS) - 1;

function keyOf(cx: number, cy: number): number {
  return ((cx & CELL_MASK) << CELL_BITS) | (cy & CELL_MASK);
}

/** The empty index, so callers never handle a `null` one. */
export const EMPTY_INDEX: SpatialIndex = {
  cell: DEFAULT_CELL,
  cells: new Map(),
  placement: new Map(),
  unbounded: [],
};

/**
 * Builds the index for a whole scene.
 *
 * The cell size is the **median** extent of the bounded values, not the mean. A single
 * enormous circle round a diagram of small triangles would drag the mean up until every
 * triangle shared one cell; the median ignores it, which is the point.
 */
export function buildIndex(values: ReadonlyMap<EntityId, Evaluated | null>): SpatialIndex {
  const boxes = new Map<EntityId, Bounds>();
  const unbounded: EntityId[] = [];
  const extents: number[] = [];

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const [id, value] of values) {
    if (value === null) continue;
    const box = pickBoundsOf(value);
    if (box === null) {
      unbounded.push(id);
      continue;
    }
    boxes.set(id, box);
    extents.push(Math.max(box.maxX - box.minX, box.maxY - box.minY));

    minX = Math.min(minX, box.minX);
    minY = Math.min(minY, box.minY);
    maxX = Math.max(maxX, box.maxX);
    maxY = Math.max(maxY, box.maxY);
  }

  const spread = boxes.size === 0 ? 0 : Math.max(maxX - minX, maxY - minY);
  const cell = chooseCell(extents, spread, boxes.size);
  const cells = new Map<number, EntityId[]>();
  const placement = new Map<EntityId, readonly number[]>();

  for (const [id, box] of boxes) {
    place(id, box, cell, cells, placement, unbounded);
  }

  return { cell, cells, placement, unbounded };
}

/**
 * The index after `changed` entities were re-evaluated.
 *
 * Returns a new index sharing every untouched cell with the old one. Copy-on-write per cell
 * rather than a deep copy is what keeps this cheaper than the value map copy that
 * `evaluateFrom` already performs — so incremental evaluation gains an index without gaining
 * an order of growth.
 *
 * The cell size is deliberately **not** recomputed. Re-deriving it would invalidate every
 * placement and turn an incremental update back into a rebuild, and a drag does not change
 * the scale of a drawing. A structural edit goes through `buildIndex`, which does re-derive.
 */
export function updateIndex(
  index: SpatialIndex,
  values: ReadonlyMap<EntityId, Evaluated | null>,
  changed: Iterable<EntityId>
): SpatialIndex {
  const touched = [...changed];
  if (touched.length === 0) return index;

  const cells = new Map(index.cells);
  const placement = new Map(index.placement);
  let unbounded = index.unbounded;
  const copied = new Set<number>();

  /**
   * Cells are shared with the old index until the moment one is written to.
   *
   * The `existing === undefined` arm is not defensive padding — it is the fix for a real
   * corruption. Removal deletes a cell that has become empty, so a key can be *already
   * copied* and *absent from the map* at once. Keying only on `copied` then handed back a
   * fresh array that was never stored, and every insert into that cell went nowhere: an
   * entity dragged from one cell to another simply vanished from the index. The symptom was
   * a point that could not be grabbed a second time, three layers away from here.
   */
  const mutable = (key: number): EntityId[] => {
    const existing = cells.get(key);
    if (copied.has(key) && existing !== undefined) return existing as EntityId[];

    const own = existing === undefined ? [] : [...existing];
    copied.add(key);
    cells.set(key, own);
    return own;
  };

  for (const id of touched) {
    for (const key of placement.get(id) ?? []) {
      const bucket = mutable(key);
      const at = bucket.indexOf(id);
      if (at >= 0) bucket.splice(at, 1);
      if (bucket.length === 0) cells.delete(key);
    }
    placement.delete(id);

    if (unbounded.includes(id)) unbounded = unbounded.filter((other) => other !== id);
  }

  const added: EntityId[] = [];
  for (const id of touched) {
    const value = values.get(id) ?? null;
    if (value === null) continue;

    const box = pickBoundsOf(value);
    if (box === null) {
      added.push(id);
      continue;
    }
    placeInto(id, box, index.cell, mutable, placement, added);
  }

  return {
    cell: index.cell,
    cells,
    placement,
    unbounded: added.length === 0 ? unbounded : [...unbounded, ...added],
  };
}

/**
 * Every entity that might lie within `radius` of `world`, plus some that do not.
 *
 * Order is unspecified — the caller ranks. Returning a `Set` rather than an array is what
 * keeps an entity spanning several cells from being measured several times.
 */
export function queryIndex(
  index: SpatialIndex,
  world: { readonly x: number; readonly y: number },
  radius: number
): ReadonlySet<EntityId> {
  const found = new Set<EntityId>(index.unbounded);

  const minX = Math.floor((world.x - radius) / index.cell);
  const maxX = Math.floor((world.x + radius) / index.cell);
  const minY = Math.floor((world.y - radius) / index.cell);
  const maxY = Math.floor((world.y + radius) / index.cell);

  for (let cx = minX; cx <= maxX; cx += 1) {
    for (let cy = minY; cy <= maxY; cy += 1) {
      for (const id of index.cells.get(keyOf(cx, cy)) ?? []) found.add(id);
    }
  }

  return found;
}

/** Every entity whose bounds touch `box`, plus some that do not. Used for viewport culling. */
export function queryIndexBounds(index: SpatialIndex, box: Bounds): ReadonlySet<EntityId> {
  const found = new Set<EntityId>(index.unbounded);

  const minX = Math.floor(box.minX / index.cell);
  const maxX = Math.floor(box.maxX / index.cell);
  const minY = Math.floor(box.minY / index.cell);
  const maxY = Math.floor(box.maxY / index.cell);

  /*
   * A valve, not a rule.
   *
   * A viewport zoomed far out spans an unbounded number of cells, and walking them would make
   * culling cost more than the drawing it saves. Past a fixed count, hand back everything.
   *
   * The threshold is absolute rather than relative to the index size, which is what it was
   * first: `spanned > cells.size` meant a two-entity document surrendered after 3 cells, so a
   * query anywhere near a small drawing returned the whole thing. Empty cells are cheap to
   * skip — what has to be bounded is the *number* of them, not their ratio to anything.
   */
  const spanned = (maxX - minX + 1) * (maxY - minY + 1);
  if (spanned > MAX_CELLS_SCANNED) {
    for (const ids of index.cells.values()) for (const id of ids) found.add(id);
    return found;
  }

  for (let cx = minX; cx <= maxX; cx += 1) {
    for (let cy = minY; cy <= maxY; cy += 1) {
      for (const id of index.cells.get(keyOf(cx, cy)) ?? []) found.add(id);
    }
  }

  return found;
}

/* ------------------------------------------------------------------ internals */

function chooseCell(extents: readonly number[], spread: number, count: number): number {
  /*
   * ## Why the quantile, and why only the non-degenerate extents
   *
   * The first rule was "twice the median extent", and it was measured to do nothing at all:
   * a geometry drawing is mostly *points*, whose extent is exactly zero, so the median of a
   * document of 4 000 points and 4 000 segments is zero. The cell size collapsed to its floor
   * of one world unit, every segment then spanned more cells than the budget allowed, and all
   * 4 000 of them were filed as "always check" — an index that had been carefully built and
   * was equivalent to no index at all. It cost 20% and returned nothing.
   *
   * So: ignore zero extents, which describe things that fit in any cell whatsoever, and take
   * a **high quantile** of the rest rather than their middle. The middle is the wrong statistic
   * because the cost is not symmetric — a cell too large merely widens the candidate set, while
   * a cell too small pushes big objects out of the grid entirely. The 75th percentile keeps
   * three quarters of the drawing inside a single cell and lets the long tail spill over a few.
   */
  const positive = extents.filter((extent) => extent > 0).sort((a, b) => a - b);

  if (positive.length > 0) {
    const at = Math.min(positive.length - 1, Math.floor(positive.length * 0.75));
    return Math.max((positive[at] ?? DEFAULT_CELL) * 2, 1e-6);
  }

  /*
   * Nothing has any extent: a document of points alone. Size the cell so a cell holds a
   * handful of them, which is what makes the grid worth consulting.
   */
  if (count < 2 || spread <= 0) return DEFAULT_CELL;
  return Math.max(spread / Math.sqrt(count), 1e-6);
}

function place(
  id: EntityId,
  box: Bounds,
  cell: number,
  cells: Map<number, EntityId[]>,
  placement: Map<EntityId, readonly number[]>,
  unbounded: EntityId[]
): void {
  placeInto(
    id,
    box,
    cell,
    (key) => {
      const existing = cells.get(key);
      if (existing !== undefined) return existing;
      const own: EntityId[] = [];
      cells.set(key, own);
      return own;
    },
    placement,
    unbounded
  );
}

function placeInto(
  id: EntityId,
  box: Bounds,
  cell: number,
  bucketFor: (key: number) => EntityId[],
  placement: Map<EntityId, readonly number[]>,
  unbounded: EntityId[]
): void {
  const minX = Math.floor(box.minX / cell);
  const maxX = Math.floor(box.maxX / cell);
  const minY = Math.floor(box.minY / cell);
  const maxY = Math.floor(box.maxY / cell);

  /*
   * A value far larger than the cell size — a circle around the whole figure — would occupy
   * hundreds of cells, and would have to be removed from all of them on every update. Such a
   * value is cheaper to treat as unbounded: always considered, never placed.
   */
  if ((maxX - minX + 1) * (maxY - minY + 1) > MAX_CELLS_PER_ENTITY) {
    unbounded.push(id);
    return;
  }

  const keys: number[] = [];
  for (let cx = minX; cx <= maxX; cx += 1) {
    for (let cy = minY; cy <= maxY; cy += 1) {
      const key = keyOf(cx, cy);
      bucketFor(key).push(id);
      keys.push(key);
    }
  }
  placement.set(id, keys);
}
